// src/components/UserEstacionesList.js
import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { fetchUserCategory } from '../services/apiService';
import 'react-circular-progressbar/dist/styles.css';
import { categoryKeysForProgressTotals } from '../categories/categoryMappings';
import categoryIdMapping from '../categories/categoryIdMapping';

function UserEstacionesList({
  users, onImpersonate, authTokens,
}) {
  const [totalIndicators, setTotalIndicators] = useState({});
  const [currentSlides, setCurrentSlides] = useState({});

  const fetchUserCategoryData = useCallback(async (userId, category) => {
    if (!categoryIdMapping[category]) return undefined;

    const categoryId = categoryIdMapping[category];
    try {
      const userCategory = await fetchUserCategory(authTokens, userId, categoryId);
      return userCategory;
    } catch (error) {
      console.error(`Error fetching category data for ${category}:`, error);
      return undefined;
    }
  }, [authTokens]);

  useEffect(() => {
    const loadUserServices = async () => {
      const totals = {};

      await Promise.all(users.map(async (user) => {
        const userTotals = {};

        await Promise.all(user.services.map(async (service) => {
          const serviceTotals = {
            documents_uploaded: 0,
            documents_completed: 0,
            documents_total: 0,
          };
          const categories = categoryKeysForProgressTotals(service.name);

          await Promise.all(categories.map(async (category) => {
            const userCategory = await fetchUserCategoryData(user.id, category);
            if (userCategory) {
              serviceTotals.documents_uploaded += userCategory.documents_uploaded || 0;
              serviceTotals.documents_completed += userCategory.documents_completed || 0;
              serviceTotals.documents_total += userCategory.documents_total || 0;
            }
          }));

          userTotals[service.name] = serviceTotals;
        }));

        totals[user.id] = userTotals;
      }));

      setTotalIndicators(totals);

      // Initialize carousel positions
      const initialSlides = {};
      users.forEach((user) => {
        initialSlides[user.id] = 0;
      });
      setCurrentSlides(initialSlides);
    };

    if (users.length > 0 && authTokens) {
      loadUserServices();
    }
  }, [users, fetchUserCategoryData, authTokens]);

  const getProgressColor = (uploaded, total) => {
    if (total === 0) return '#9CA3AF'; // Gris para casos de 0 de 0
    const percentage = (uploaded / total) * 100;
    if (percentage < 25) return '#EF4444';
    if (percentage < 50) return '#F59E0B';
    if (percentage < 99) return '#10B981';
    return '#059669';
  };

  const handlePrevSlide = (userId) => {
    setCurrentSlides((prev) => ({
      ...prev,
      [userId]: Math.max(0, prev[userId] - 1),
    }));
  };

  const handleNextSlide = (userId, totalServices) => {
    setCurrentSlides((prev) => ({
      ...prev,
      [userId]: Math.min(Math.ceil(totalServices / 4) - 1, prev[userId] + 1),
    }));
  };

  // Ordenar los usuarios alfabéticamente por nombre de usuario
  const sortedUsers = [...users].sort((a, b) => a.username.localeCompare(b.username));

  return (
    <div className="w-full p-4">
      <div className="space-y-8">
        {sortedUsers.map((user) => {
          const totalSlides = Math.ceil(user.services.length / 4);
          const currentSlide = currentSlides[user.id] || 0;
          const visibleServices = user.services.slice(currentSlide * 4, (currentSlide + 1) * 4);

          const navButtonBaseClass = `
            absolute top-1/2 transform -translate-y-1/2 bg-white rounded-full p-2
            shadow-md hover:shadow-lg transition-all duration-200 z-10
          `;

          return (
            <div
              key={user.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800">{user.username}</h3>
                    <p className="text-sm text-gray-500 mt-1">Estación de Servicio</p>
                  </div>
                  {onImpersonate && (
                    <button
                      onClick={() => onImpersonate(user.id)}
                      type="button"
                      className="bg-blue-500 hover:bg-blue-600
                      text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors duration-200"
                    >
                      Acceder
                    </button>
                  )}
                </div>
              </div>

              <div className="p-6">
                <div className="relative">
                  {totalSlides > 1 && currentSlide > 0 && (
                    <button
                      type="button"
                      onClick={() => handlePrevSlide(user.id)}
                      className={`${navButtonBaseClass} -left-4`}
                      aria-label="Anterior"
                    >
                      <FaChevronLeft className="text-gray-600 w-4 h-4" />
                    </button>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {visibleServices.map((service) => (
                      <div
                        key={service.id}
                        className="bg-white rounded-lg p-4 shadow
                        hover:shadow-md transition-all duration-200 transform hover:-translate-y-1"
                      >
                        <h4 className="text-sm font-medium text-gray-800 mb-3 truncate">
                          {service.name}
                        </h4>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div style={{ width: '60px', height: '60px' }}>
                              <CircularProgressbar
                                value={
                                  (totalIndicators[user.id]?.[service.name]?.documents_uploaded
                                  / totalIndicators[user.id]?.[service.name]?.documents_total) * 100 || 0
                                }
                                styles={buildStyles({
                                  pathColor: getProgressColor(
                                    totalIndicators[user.id]?.[service.name]?.documents_uploaded,
                                    totalIndicators[user.id]?.[service.name]?.documents_total,
                                  ),
                                  trailColor: '#F3F4F6',
                                })}
                              />
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-medium text-gray-600">Subidos</p>
                              <p className="text-sm font-bold text-gray-800">
                                {totalIndicators[user.id]?.[service.name]?.documents_uploaded || 0}
                                {' '}
                                /
                                {' '}
                                {totalIndicators[user.id]?.[service.name]?.documents_total || 0}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div style={{ width: '60px', height: '60px' }}>
                              <CircularProgressbar
                                value={
                                  (totalIndicators[user.id]?.[service.name]?.documents_completed
                                  / totalIndicators[user.id]?.[service.name]?.documents_total) * 100 || 0
                                }
                                styles={buildStyles({
                                  pathColor: getProgressColor(
                                    totalIndicators[user.id]?.[service.name]?.documents_completed,
                                    totalIndicators[user.id]?.[service.name]?.documents_total,
                                  ),
                                  trailColor: '#F3F4F6',
                                })}
                              />
                            </div>
                            <div className="text-right">
                              <p className="text-xs font-medium text-gray-600">Completados</p>
                              <p className="text-sm font-bold text-gray-800">
                                {totalIndicators[user.id]?.[service.name]?.documents_completed || 0}
                                {' '}
                                /
                                {' '}
                                {totalIndicators[user.id]?.[service.name]?.documents_total || 0}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {totalSlides > 1 && currentSlide < totalSlides - 1 && (
                    <button
                      type="button"
                      onClick={() => handleNextSlide(user.id, user.services.length)}
                      className={`${navButtonBaseClass} -right-4`}
                      aria-label="Siguiente"
                    >
                      <FaChevronRight className="text-gray-600 w-4 h-4" />
                    </button>
                  )}
                </div>

                {totalSlides > 1 && (
                  <div className="flex justify-center mt-4 space-x-2">
                    {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                      <button
                        key={`carousel-${user.id}-${slideIndex + 1}`}
                        type="button"
                        onClick={() => setCurrentSlides((prev) => ({ ...prev, [user.id]: slideIndex }))}
                        className={`h-2 rounded-full transition-all duration-200 ${
                          currentSlide === slideIndex ? 'bg-blue-500 w-4' : 'bg-gray-300 w-2'
                        }`}
                        aria-label={`Ir a la página ${slideIndex + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

UserEstacionesList.propTypes = {
  users: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    username: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    services: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      categories: PropTypes.arrayOf(PropTypes.string),
    })).isRequired,
  })).isRequired,
  onImpersonate: PropTypes.func.isRequired,
  authTokens: PropTypes.shape({
    accessToken: PropTypes.string,
  }).isRequired,
};

export default UserEstacionesList;
